import type {
  PokerMttQueryVariables,
  PokerPowerPath,
  PokerTournamentNameColourVariables
} from '@ps/web-martech-cms-types';
import type { Selector } from 'react-redux';

import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  CommonQueryVariableOptions,
  ConfigQueryVariablesPayload,
  FilterVariables,
  Tournament,
  TournamentColourMap
} from '@swf/poker';
import { generateMTTSelectors, mapQueryVariables } from '@swf/poker';

import type { StepKey } from '../types';
import { filterTournaments, normalizePassesResponse } from '../utils';

import type { PokerPowerPathState } from './types';

const getConfigTournNameColours = (rootState: RootState, widget: PokerPowerPath) => {
  const tournNameColourUid = widget.tournament_name_colour_variables?.[0]?.uid;

  const colorData = select.cms.getContentItem(
    rootState,
    tournNameColourUid
  ) as PokerTournamentNameColourVariables;

  return colorData;
};

export const selectors = (slice: Slicer<PokerPowerPathState>, createSelector: SelectorCreator) => ({
  ...generateMTTSelectors<PokerPowerPathState>(slice),
  hasPreloaded: () => slice((state) => state.hasPreloaded),
  getTournamentList(this: { getFilterVariables: Selector<RootState, FilterVariables> }) {
    return createSelector(slice, this.getFilterVariables, (state, filterVariables): Tournament[] =>
      filterTournaments(filterVariables, Object.values(state.tournamentsData))
    );
  },
  isLoading: () => slice((state) => state.isLoading),
  getStepByKey: () =>
    createSelector(
      slice,
      (_: RootState, stepKey: StepKey) => stepKey,
      (state, stepKey) => state.steps[stepKey]
    ),
  getPasses: () =>
    createSelector(
      slice,
      (rootState: RootState) => select.cms.getSiteName(rootState),
      (rootState: RootState) =>
        select.account.getCountry(rootState) ?? select.country.getCountry(rootState),
      (state, site, country) => normalizePassesResponse(state.passesData, site, country)
    ),
  getTableRowsCount(this: { getTournamentList: Selector<RootState, Tournament[]> }) {
    return createSelector(
      slice,
      this.getTournamentList,
      (_, tournaments): number => tournaments?.length
    );
  },
  getConfigQueryVariables(this: {
    getCommonVariables: Selector<RootState, CommonQueryVariableOptions>;
  }) {
    return createSelector(
      slice,
      this.getCommonVariables,
      (rootState: RootState, { widget }: ConfigQueryVariablesPayload<PokerPowerPath>) => {
        const filterVariablesUid = widget.filter_variables?.[0]?.uid;
        return filterVariablesUid
          ? (select.cms.getContentItem(rootState, filterVariablesUid) as
              | PokerMttQueryVariables
              | undefined)
          : undefined;
      },
      (_, commonVariables, cmsVariables) =>
        mapQueryVariables(
          {
            ...commonVariables,
            excludeSnG: false,
            isPlayMoney: false // PowerPath does not support Play money
          },
          cmsVariables
        )
    );
  },
  getTournamentsNameColours() {
    return createSelector(
      slice,
      (rootState: RootState, widget: PokerPowerPath) =>
        getConfigTournNameColours(rootState, widget),
      (_, colourData) => {
        const colourMap = (colourData?.tournament_name_colour ?? []).reduce<TournamentColourMap>(
          (acc, item) => {
            const variable = item.colour_variable?.variable;
            const color = item.colour_variable?.colour;

            if (variable && color) {
              acc[variable] = color;
            }

            return acc;
          },
          {}
        );

        return colourMap;
      }
    );
  }
});
